import { ViewSettings } from './models';
import { SELECT_EDITOR_TAB } from './actions';
import { DETECT_OPTIONS } from '../facesTabModule';
import { DEFAULT_FILTER_PARAMETERS } from '@helpers/constants';
import {
  DetectionAssetState,
  Extra,
  MaxDepthArray,
  Undo,
} from './store.models';

export const TDO_SETTINGS_NAMESPACE = 'tdoSettings';

const detectionAssetState: DetectionAssetState = {
  loaded: false,
  currentPosition: 0,
  previousPosition: 0,
  playheadJumped: false,
  tdo: null,
  tdoIsChanged: false,
  saveRev: 0,
  tdoHasPermissions: undefined,

  detectionClusterGroups: {},
  udrClusterGroups: {},
  clusterMap: {},

  detectionCollections: undefined,
  transcription: undefined,
  transcriptionView: {
    selected: [],
    voiceMaskEdits: [],
    audioRedactionEdits: [],
    selectedAudioEditSlice: undefined,
    selectedAudioEditWord: undefined, // similar to selectedAudioEditSlice (might combine in future)
    editBufferOpen: false,
    editNotesOpen: false,
    currentEditWords: undefined,
  },
  progress: {
    udrs: 0,
    faceDetection: 0,
    transcription: 0,
  },

  facesSelectedEngineId: null,

  backgroundBlurEdits: [],

  detectionSeriesLengthWithDeleted: undefined,
  isTdoExternallyModified: false,
  hasInvalidTdoData: undefined,
};

const viewSettingsState: ViewSettings = {
  playbackSpeed: 1,
  playbackDirection: 'forward',
  overlayPreview: 'outline',
  overlayTool: 'redaction',
  audioPreview: 'redacted',
  selectedPolyGroups: {},
  clusterMergeGroupIds: {},
  clusterMergeSegments: {},
  expandedPolyGroups: {},
  sortPolyGroupsBy: {
    column: 'startTimeMs',
    direction: 'asc',
  },
  filterParameters: DEFAULT_FILTER_PARAMETERS,
  showGlobalSettingsModal: false,
  onscreenOnlyFilter: false,
  onscreenOnlyFilterUserSet: false,
  highlightedOverlay: undefined,
  prevMarkerTime: 0,
  draggedOverlayId: null,
  fillBadgeEditAnchor: null,
};

export type MainStore = DetectionAssetState & ViewSettings & Extra & Undo;

// TODO Finish store models.
export const defaultState: MainStore = {
  ...detectionAssetState,
  ...viewSettingsState,

  user: undefined,

  fetchingTdo: false,
  failedFetchingTdo: false,

  /**
   * VE-30516: did the TDO arrive from the server WITH its own `details.settings`?
   *
   * `onFetchTDOSuccess` materialises `settings` from DEFAULT_GLOBAL_SETTINGS on
   * every load, so by the time anything reads state it is impossible to tell a
   * file that has saved settings from one that has none. The seed needs exactly
   * that distinction and used to approximate it with `settings.isSaved` — a flag
   * living inside the object it describes, which every settings writer could
   * destroy, and did.
   */
  tdoSettingsFromServer: false,

  // IDs removed locally but not yet confirmed deleted on the server. Filtered
  // from FETCH_REDACTED_MEDIA_SUCCESS so a racing refetch can't re-introduce
  // assets we just deleted.
  pendingDeletedRedactedMediaIds: [] as string[],
  pendingDeletedAuditLogIds: [] as string[],

  hasAudio: true,
  videoLoaded: false,
  thumbnailTracks: null,
  shakaPlayer: null,
  faceDetection: {
    isFetching: false,
    isProcessing: false,
    isReady: false,
    statusMessage: '',
  },

  redactionMediaDataReady: false,

  udrCollection: undefined,
  textOverlayCollection: undefined,

  hasSavedUdrs: undefined,
  hasSavedDetections: undefined,
  udrClusterGroupsLoaded: false,
  detectionClusterGroupsLoaded: false,

  udrsState: {
    udrBoundingPolyBeingUpdated: undefined,
    selectedUDRGroupId: undefined,
    udrAsset: {
      boundingPolys: {},
    },
    localUDRAsset: {
      boundingPolys: {},
    },
    regularModePlaybackSpeed: 1,
  },

  audiowaves: [],

  //  License Plates engine results tab
  licensePlatesEngineResults: null,
  licensePlatesSelectedEngineId: null,
  selectedLicensePlatesGroup: null,

  // SAVE button
  fetchingUpdateAsset: false,
  failedFetchingUpdateAsset: false,

  mediaDetailsPageSelectedTab: SELECT_EDITOR_TAB.type,
  isShowConfirmLeave: false,

  numberOfRedactedFile: 0,
  // position bar DND
  dragAndDropPositionBar: false,

  // update status
  fetchingUpdateStatus: false,
  failedUpdateStatus: false,

  // redacted asset play
  redactedAssetStarted: false,

  // download
  downloadStarted: false,
  fetchingCreateDownloadTask: false,
  pollingDownload: false,

  // pollingFileWatermark: new Date(),
  pollingDownloadStopped: false,
  downloadJobStatus: false,
  downloadJob: null,

  // Update MDP after Redaction job completes
  gettingRedactionJobStatus: false,
  failedGettingRedactionJobStatus: false,
  redactionJobStatus: false,
  redactionJobCreatedDateTime: null,
  redactionJob: null,

  // HEAD DETECTION BUTTON
  buttonDetectFaceDisable: false,

  /**
   * TRANSCRIPTION BUTTON — really "a transcription is in progress", of which
   * the Run button being disabled is one consequence. Raised when a run starts
   * and held until its transcript has been fetched, so the audio tab and the
   * toolbar cannot disagree about whether anything is running (VE-30784).
   */
  buttonTranscriptionDisable: false,

  /**
   * Has the speaker-separation fetch for the open media answered yet?
   *
   * Only meaningful while a separation run has completed on this TDO — see
   * selectAudioTabState, which is the one reader. False until the worker
   * reports back, and reset on every media switch.
   */
  speakerSeparationFetchSettled: false,

  dataFetchedForDetectionType: {
    laptop: false,
    head: false,
    poim: false,
    licensePlate: false,
    card: false,
    notepad: false,
    person: false,
  },

  integrations: {
    govQa: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
    foiaXpress: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
    casepoint: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
    exterro: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
    nuix: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
    externalIntegration: {
      isFormShown: false,
      isSendingFile: false,
      isStatusDialogShown: false,
    },
  },

  deletedOverlaysCache: {
    overlayIdsMap: {},
    overlaySubsegmentIdsMap: {},
  },

  caseMedia: {
    caseId: '',
    caseName: '',
    media: [],
  },

  downloadUrl: '',

  isCaseMediaDrawerOpen: false,

  mediaComments: {},
  mediaCommentDrafts: {},
  editingMediaCommentId: null,
  isHideComment: false,
  mediaCommentsUsers: {},
  isFetchingComments: false,
  selectedMediaComments: [],
  lastSaveMediaDataDisplayTime: '',
  isSaveRunning: false,
  isSaveFailed: false,
  isBannerDismissed: false,
  pendingLockedStatus: null,
  pendingLockedStatusNote: null,
  pendingLockedStatusResponse: null,
  pendingLockedStatusResponseType: null,
  isBasicUserQuerySupported: true,
  isPlayerSeeked: false,

  tdoLockInfo: {
    isLocked: false, // TODO this name is confusing - means is Locked by someone else!!!
    name: undefined,
  },
  externalRedactionStatus: null,
  mergedSetBlock: null,
  detectObjects: {
    [DETECT_OPTIONS.HEAD_OBJECTS]: false,
    [DETECT_OPTIONS.PERSON_OBJECTS]: false,
  },
  showDetectOptionsDialog: false,
  tdoLocks: [],

  history: {
    past: new MaxDepthArray(),
    future: [],
  },
};

export const namespace = 'vtn-redact-state-mediaDetails';

export interface RedactMediaDetailsState {
  [namespace]: MainStore;
}
